import { defineStore } from "pinia"
import { api, type Customer } from "../lib/api"
import { forgetBuyer, identifyBuyer, identifyKlaviyo } from "../lib/tracking"

// Customer session. The access token lives in memory only (15m, re-minted from
// the refresh token); the opaque refresh token is persisted so a reload restores
// the session. Refresh rotation + family reuse detection are enforced server-side.
const REFRESH_KEY = `dk:refresh:${location.host}`

/** The customer as the ad pixels' identity — the same server-verified email
 *  identifyKlaviyo gets, plus the name the account holds. */
const buyerOf = (c: Customer) => ({ email: c.email, first_name: c.first_name, last_name: c.last_name })

export const useAccountStore = defineStore("account", {
  state: () => ({
    accessToken: "",
    refresh: localStorage.getItem(REFRESH_KEY) ?? "",
    customer: null as Customer | null,
    ready: false, // initial restore attempt finished
  }),
  getters: {
    isAuthed: (s) => !!s.customer,
    name: (s) => (s.customer ? `${s.customer.first_name} ${s.customer.last_name}`.trim() || s.customer.email : ""),
  },
  actions: {
    setSession(customer: Customer, tokens: { access_token: string; refresh_token: string }) {
      this.customer = customer
      this.accessToken = tokens.access_token
      this.refresh = tokens.refresh_token
      localStorage.setItem(REFRESH_KEY, tokens.refresh_token)
      // A server-verified email — the one case identifyKlaviyo's plaintext
      // contract requires. Gated on consent internally; safe to call whether
      // or not Klaviyo is even connected for this store.
      identifyKlaviyo(customer.email)
      identifyBuyer(buyerOf(customer))
    },
    clear() {
      this.customer = null
      this.accessToken = ""
      this.refresh = ""
      localStorage.removeItem(REFRESH_KEY)
    },
    /** Restore a session from the persisted refresh token on app boot. */
    async restore() {
      if (this.customer) return this.customer
      if (this.refresh) {
        try {
          const { tokens } = await api.refresh(this.refresh)
          this.accessToken = tokens.access_token
          this.refresh = tokens.refresh_token
          localStorage.setItem(REFRESH_KEY, tokens.refresh_token)
          const { customer } = await api.me(this.accessToken)
          this.customer = customer
          // A returning, already-authenticated visitor deserves the same
          // identification a fresh login gets — this is the reload path.
          identifyKlaviyo(customer.email)
          identifyBuyer(buyerOf(customer))
        } catch {
          this.clear()
        }
      }
      this.ready = true
      return this.customer
    },
    async login(email: string, password: string) {
      const { customer, tokens } = await api.login(email, password)
      this.setSession(customer, tokens)
    },
    async register(body: {
      email: string
      password: string
      first_name?: string
      last_name?: string
      accepts_marketing?: boolean
    }) {
      const { customer, tokens } = await api.register(body)
      this.setSession(customer, tokens)
    },
    async logout() {
      const rt = this.refresh
      this.clear()
      // Not in clear(): that also runs when a stale session fails to restore, which
      // is the same person coming back, not a different one taking over the browser.
      forgetBuyer()
      if (rt) {
        try {
          await api.logout(rt)
        } catch {
          /* best-effort */
        }
      }
    },
  },
})
